import styled from 'styled-components';
import { Z_INDEX_HIGH } from '@mews-ui/theme';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken, Size } from '@optimus-web/common';
import { StyledTransitionGroup, StyledTransitionGroupProps, TransitionGroupChildBaseProps } from '@optimus-web/helpers';
import { ANIMATION_CURVE, ANIMATION_DURATION, MEDIA_QUERY } from '@optimus-web/local-tokens';

export const getSidepanelWidth = ({ width }: { width: Size }) => {
    switch (width) {
        case 'small':
            return 468;
        case 'large':
            return 928;
        default:
            return 698;
    }
};

export const SidePanelWrapper = styled((props: Partial<StyledTransitionGroupProps<TransitionGroupChildBaseProps>>) => (
    <StyledTransitionGroup {...props} />
))`
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${Z_INDEX_HIGH};
    pointer-events: none;
    overflow: hidden;
`;

interface SidePanelContainer {
    opened: boolean;
    width: Size;
}

export const SidePanelContainer = styled.div<SidePanelContainer>`
    background-color: ${getToken(TokenName.BACKGROUND_STATIC_FLAT)};
    border-left: ${getToken(TokenName.BORDER_WIDTH_100)}px ${getToken(TokenName.BORDER_STYLE_SOLID_STYLE)}
        ${getToken(TokenName.BORDER_STATIC_SECONDARY)};
    position: absolute;
    bottom: 0;
    right: 0;
    transition: transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};
    ${(props) => `transform: translateX(${props.opened ? '0' : '100%'});`}
    height: 100%;
    width: 100%;
    max-width: ${getSidepanelWidth}px;
    pointer-events: initial;
    display: flex;
    flex-direction: column;

    ${({ width, theme }) =>
        width &&
        `
        ${width !== 'small' && MEDIA_QUERY['up-to-medium']} {
            max-width: calc(100% - ${getToken(TokenName.SPACING_900)({ theme })}px);
        }
    `}
`;

export const SidePanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${getToken(TokenName.SPACING_200)}px;
    margin: ${getToken(TokenName.SPACING_300)}px;
`;

export const SidePanelHeaderTitle = styled.div`
    display: flex;
    gap: ${getToken(TokenName.SPACING_150)}px;
    align-items: center;
    flex-grow: 1;
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    ${getTextTokenPreset('TITLE_S')};
`;

export const SidePanelHeaderAction = styled.div`
    display: flex;
`;

export const SidePanelContent = styled.div`
    padding: 0 ${getToken(TokenName.SPACING_300)}px;
    flex-grow: 1;
    overflow-y: auto;
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
`;

interface SidePanelActionProps {
    hasScrollBar: boolean;
}

export const SidePanelAction = styled.div<SidePanelActionProps>`
    padding: calc(${getToken(TokenName.SPACING_300)}px - ${getToken(TokenName.BORDER_WIDTH_100)}px)
        ${getToken(TokenName.SPACING_300)}px ${getToken(TokenName.SPACING_300)}px;
    border-top: ${getToken(TokenName.BORDER_WIDTH_100)}px ${getToken(TokenName.BORDER_STYLE_SOLID_STYLE)} transparent;
    ${({ hasScrollBar, theme }) =>
        hasScrollBar &&
        `
            border-color: ${getToken(TokenName.BORDER_STATIC_SECONDARY)({ theme })};
            background-color: ${getToken(TokenName.BACKGROUND_STATIC_RAISED)({ theme })};
        `};
`;
